import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { StoreRequest } from '@bn/shared/state-store';
import { MarkAllTouchedDirective } from '@bn/web-ui';
import { AlertComponent } from '@bn/web-ui/alert/alert.component';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { ModalState } from '@bn/web-ui/dialog';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { LoadingComponent } from '@bn/web-ui/loading/loading.component';
import { FormListItem } from '@bn/web-ui/models';
import { SelectItem } from '@bn/web-ui/select/models';
import { SelectComponent } from '@bn/web-ui/select/select.component';
import { TextareaComponent } from '@bn/web-ui/textarea/textarea.component';
import { AccessListType } from '../../openapi';
import { ShieldAccessListsApiStore, ShieldApiStore } from '../../stores';

@Component({
  templateUrl: './shield-create-edit-access-list-modal.component.html',
  styleUrls: ['./shield-create-edit-access-list-modal.component.scss'],
  imports: [
    AlertComponent,
    ButtonComponent,
    InputComponent,
    LoadingComponent,
    SelectComponent,
    TextareaComponent,
    FaIconComponent,
    MarkAllTouchedDirective,
    ReactiveFormsModule,
    NgClass,
  ],
})
export class ShieldCreateEditAccessListModal extends AbstractLazyDirective implements OnInit, OnDestroy {
  private readonly dialog = inject<BsModalRef<ShieldCreateEditAccessListModal>>(BsModalRef);

  readonly #shieldStore = inject(ShieldApiStore);
  readonly #shieldAccessListsStore = inject(ShieldAccessListsApiStore);

  readonly isAddError = this.#shieldAccessListsStore.isError(StoreRequest.Add, ['save-modal']);
  readonly getAddErrorMessage = this.#shieldAccessListsStore.getErrorMessage(StoreRequest.Add, ['save-modal']);
  readonly isAddLoading = this.#shieldAccessListsStore.isLoading(StoreRequest.Add, ['save-modal']);
  readonly isAdded = this.#shieldAccessListsStore.isFinished(StoreRequest.Add, ['save-modal']);

  readonly isUpdateError = this.#shieldAccessListsStore.isError(StoreRequest.Update, ['save-modal']);
  readonly isUpdateLoading = this.#shieldAccessListsStore.isLoading(StoreRequest.Update, ['save-modal']);
  readonly getUpdateErrorMessage = this.#shieldAccessListsStore.getErrorMessage(StoreRequest.Update, ['save-modal']);
  readonly isUpdated = this.#shieldAccessListsStore.isFinished(StoreRequest.Update, ['save-modal']);

  readonly isDetailsLoading = this.#shieldAccessListsStore.isLoading(StoreRequest.Item);
  readonly isDetailsLoaded = this.#shieldAccessListsStore.isFinished(StoreRequest.Item);

  public data?: ModalState;
  public isEdit? = false;
  public id?: number;
  public isDragging = false;
  public errorMessage?: string;
  public icon?: IconProp;

  public listTypeItems: SelectItem[] = [
    { Id: 0, Name: 'IP Addresses' },
    { Id: 1, Name: 'CIDR Blocks' },
    { Id: 2, Name: 'ASNs' },
    { Id: 3, Name: 'Countries' },
    { Id: 4, Name: 'Organizations' },
    { Id: 5, Name: 'JA4 Fingerprints' },
  ];

  public listActionItems: FormListItem[] = [
    { label: 'Block', value: 0, name: 'Block' },
    { label: 'Challenge', value: 1, name: 'Challenge' },
    { label: 'Log', value: 2, name: 'Log' },
    { label: 'Allow', value: 3, name: 'Allow' },
  ];

  public form = new FormGroup({
    content: new FormControl('', [Validators.required]),
    name: new FormControl('', [Validators.required]),
    listType: new FormControl<number | null>(null, [Validators.required]),
    listAction: new FormControl(0),
  });

  public get selectedTypeName(): string {
    const typeValue = this.form.controls.listType.value;
    if (typeValue == null) return 'items';
    return this.listTypeItems.find((item) => item.Id === typeValue)?.Name ?? 'items';
  }

  constructor() {
    super();
    const dialog = this.dialog;

    this.subscriptions.add(dialog.onHide?.subscribe((event) => (event === 'backdrop-click' || event === 'esc' ? this.onClose() : undefined)));

    this.listenToStoreSignals();
  }

  public ngOnInit(): void {
    this.isEdit = this.data?.extra?.isEdit as boolean;
    this.id = this.data?.extra?.id as number;
    this.icon = this.data?.extra?.icon as IconProp | undefined;

    this.errorMessage = undefined;

    if (this.isEdit && this.id) {
      this.#shieldAccessListsStore.getAccessListsDetails({
        id: this.id,
        shieldZoneId: Number(this.#shieldStore.selectedEntityId()()),
      });
    }

    if (this.isEdit) this.form.controls.listType.disable();
    else this.form.controls.listType.enable();
  }

  public ngOnDestroy(): void {
    this.#shieldAccessListsStore.clearModalStates();
  }

  public onUploadCallback(event: Event) {
    event.preventDefault();

    this.errorMessage = undefined;

    const file = Array.from((event.target as HTMLInputElement).files ?? [])?.[0];

    this.readFile(file);
  }

  public onFileDrop(event: DragEvent): void {
    event.preventDefault();

    this.errorMessage = undefined;

    const files = Array.from(event.dataTransfer?.files ?? []);
    const file = files.at(0);

    this.readFile(file);
  }

  public onDragOver(event: Event): void {
    event.preventDefault();

    this.isDragging = true;
  }

  public onDragLeave(event: Event): void {
    event.preventDefault();

    this.isDragging = false;
  }

  public onClose(): void {
    this.dialog.hide();
  }

  public onSubmit(): void {
    if (this.isEdit) {
      this.onEdit();
    } else {
      this.onAdd();
    }
  }

  private readFile(file?: File): void {
    if (!file) {
      this.errorMessage = 'File was not attached';

      return;
    }

    if (!file?.name.endsWith('.txt')) {
      this.errorMessage = 'Only .txt files are supported.';

      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const content = reader.result?.toString() ?? '';
      this.form.controls.content.setValue(content);
      this.isDragging = false;
    };

    reader.onerror = () => {
      this.isDragging = false;
      this.errorMessage = 'Error reading file';
    };

    reader.readAsText(file);
  }

  private onAdd(): void {
    const { name, listType, content } = this.form.value;

    if (!content || listType == null) return;

    this.#shieldAccessListsStore.addAccessList({
      shieldZoneId: Number(this.#shieldStore.selectedEntityId()()),
      customFields: ['save-modal'],
      data: {
        name: name ?? '',
        type: listType as AccessListType,
        content,
      },
    });
  }

  private onEdit(): void {
    const { name, content } = this.form.value;

    if (!content || !this.id) return;

    this.#shieldAccessListsStore.updateAccessListDetails({
      id: this.id,
      shieldZoneId: Number(this.#shieldStore.selectedEntityId()()),
      customFields: ['save-modal'],
      data: {
        name: name ?? '',
        content,
      },
    });
  }

  private listenToStoreSignals(): void {
    effect(() => {
      if (!this.isDetailsLoaded()) return;

      const details = this.#shieldAccessListsStore.customAccessListDetails();

      if (!details) return;

      const { name, type: listType, content } = details;

      this.form.patchValue({ name, content, listType: listType as number });
    });

    effect(() => {
      if (this.isAdded() || this.isUpdated()) this.dialog.hide();
    });
  }
}
